import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { UserIdService, WindowRef } from '@spartacus/core';
import { Router } from '@angular/router';
import { CartActions } from '../store/actions/index';
import { Cart, CartType } from '@spartacus/cart/base/root';
import { GenMultiCartFacade } from './multi-cart.facade';
import { MultiCartService, StateWithMultiCart } from '@spartacus/cart/base/core';

@Injectable()
export class GenMultiCartService extends MultiCartService implements GenMultiCartFacade {
  constructor(
    store: Store<StateWithMultiCart>,
    userIdService: UserIdService,
    private router: Router,
    protected override windowRef: WindowRef,
  ) {
    super(store, userIdService);
  }

  override loadCart({ cartId, userId, extraData }: {
    cartId: string;
    userId: string;
    extraData?: any;
  }): void {
    // ActiveCartService also loads during startup/login. Keep those requests
    // on the Auto Ship cart when reviewing it through the regular cart page.
    const navigation = this.router.getCurrentNavigation();
    const location = this.windowRef.nativeWindow?.location;
    const url = navigation
      ? (navigation.finalUrl ?? navigation.extractedUrl).toString()
      : location ? location.pathname + location.search : this.router.url;
    const tree = this.router.parseUrl(url);
    const path = tree.root.children['primary']?.segments.map(segment => segment.path).join('/');
    if (extraData?.active && path === 'cart' && tree.queryParams['autoShip'] === 'true') {
      cartId = 'autoship';
    }

    super.loadCart({ cartId, userId, extraData });
  }

  override addEntry(
    userId: string,
    cartId: string,
    productCode: string,
    quantity: number,
    itemListName?: string,
    navigatedToPdpFrom?: string,
    entrySource?: string,
  ): void {
    this.store.dispatch(
      new CartActions.GenCartAddEntry({
        userId,
        cartId,
        productCode,
        quantity,
        itemListName,
        navigatedToPdpFrom,
        entrySource
      })
    );
  }

  override addEntries(
    userId: string,
    cartId: string,
    products: Array<{ productCode: string; quantity: number, itemListName?: string, navigatedToPdpFrom?: string, entrySource?: string }>
  ): void {
    products.forEach((product) => {
      this.store.dispatch(
        new CartActions.GenCartAddEntry({
          userId,
          cartId,
          productCode: product.productCode,
          quantity: product.quantity,
          itemListName: product.itemListName,
          navigatedToPdpFrom: product.navigatedToPdpFrom,
          entrySource: product.entrySource
        })
      );
    });
  }

  override updateEntry(
    userId: string,
    cartId: string,
    entryNumber: number,
    quantity?: number,
  ): void {
    if (quantity !== undefined && quantity <= 0) {
      this.removeEntry(userId, cartId, entryNumber);
    } else {
      this.store.dispatch(
        new CartActions.GenCartUpdateEntry({
          userId,
          cartId,
          entryNumber: entryNumber.toString(),
          quantity,
        })
      );
    }
  }

  setActiveCartId(cartId: string): void {
    this.store.dispatch(new CartActions.SetActiveCartId(cartId));
  }

  setActiveCartData(cart: Cart, cartId: string): void {
    this.store.dispatch(new CartActions.SetCartData({ cart, cartId }));
    this.store.dispatch(new CartActions.SetCartTypeIndex({
      cartType: CartType.ACTIVE,
      cartId,
    }));
  }
}
